import { BaseModel } from '../../../models'

export class ModelIdentityMap {

  /**
   * Stores object with an ID (for faster retrieval)
   * pathnode: {
   *   1: Object<BaseModel> (id=1, title='hello world', ...),
   *   2: Object<BaseModel> (id=2, title='foo bar', ...)
   * },
   * usertask: {
   *   1: Object<BaseModel> (completionStatus = 0, ..)
   * }
   *
   */
  private _modelStore: { [lowercasedClassName: string]: { [id: string ]: BaseModel} }

  /**
   * Stores objects that don't have an ID yet by their type
   */
  private _transientModelStore: { [lowercasedClassName: string]: Set<BaseModel> }

  constructor() {
    this.clear()
  }

  public getModel(type: string, id: string): BaseModel {
    return this._modelStore[type] ? this._modelStore[type][id] : undefined
  }

  public setModel(model: BaseModel): void {
    if (typeof model.id === 'undefined') {
      if (typeof this._transientModelStore[model.type] !== 'object') {
        this._transientModelStore[model.type] = new Set<BaseModel>()
      }
      this._transientModelStore[model.type].add(model)
    } else {
      if (typeof this._modelStore[model.type] !== 'object') {
        this._modelStore[model.type] = {}
      }
      this._modelStore[model.type][model.id] = model
    }
  }

  public findModelsBy(type: string, searchParams: any): BaseModel[] {
    if (typeof searchParams !== 'object' || searchParams === null || Object.keys(searchParams).length === 0) {
      throw new Error('Cannot find models without any search criteria')
    }

    if (searchParams['id']) {
      return [ this.getModel(type, searchParams['id']) ]
    }

    const modelStoreSection = this._modelStore[type]
    const transientModelStoreSection = this._transientModelStore[type]

    const [modelStoreSectionExists, transientModelStoreSectionExists] = [
      typeof modelStoreSection === 'object', typeof transientModelStoreSection === 'object'
    ]

    if (!modelStoreSectionExists && !transientModelStoreSectionExists) {
      return []
    }

    const foundModels = []

    const searchKeys = Object.keys(searchParams)
    const collectMatchingModel = (storedModel) => {
      if (searchKeys.every((searchKey) => storedModel[searchKey] === searchParams[searchKey])) {
        foundModels.push(storedModel)
      }
    }

    if (modelStoreSectionExists) {
      Object.keys(modelStoreSection).forEach((modelId) => collectMatchingModel(modelStoreSection[modelId]))
    }
    if (transientModelStoreSectionExists) {
      transientModelStoreSection.forEach((storedModel) => collectMatchingModel(storedModel))
    }

    return foundModels
  }

  public findAll(type: string): BaseModel[] {
    let result = []
    const persistentStoreSection = this._modelStore[type]
    if (typeof persistentStoreSection === 'object' && persistentStoreSection !== null) {
      result = Object.keys(persistentStoreSection).map((key) => persistentStoreSection[key])
    }
    const transientStoreSection = this._transientModelStore[type]
    if (typeof transientStoreSection === 'object' && transientStoreSection !== null) {
      result = result.concat(Array.from(transientStoreSection))
    }
    return result
  }

  public deleteModel(model: BaseModel): boolean {
     return model.isPersisted() ?
        this.deletePersitedModel(model) :
        this.deleteTransientModel(model)
  }

  public clear(): void {
    this._modelStore = {}
    this._transientModelStore = {}
  }

  private deleteTransientModel(model: BaseModel): boolean {
    const transientModelStoreSection = this._transientModelStore[model.type] || new Set()
    const modelExists = transientModelStoreSection.has(model)

    if (modelExists) {
      transientModelStoreSection.delete(model)
      if (transientModelStoreSection.size === 0) {
        this._transientModelStore[model.type] = undefined
        delete this._transientModelStore[model.type]
      }
    }
    return !!modelExists
  }

  private deletePersitedModel(model: BaseModel): boolean {
    const modelExists = this.getModel(model.type, model.id)

    if (modelExists) {
      this._modelStore[model.type][model.id] = undefined
      delete this._modelStore[model.type][model.id]
    }
    return !!modelExists
  }

}



// WEBPACK FOOTER //
// ./node_modules/@mprep/shared/lib/model-repository/backends/model-identity-map/model-identity-map.ts